import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    signal,
    viewChild,
    ViewEncapsulation,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import {
    ButtonComponent,
    IconComponent,
    InfoIconComponent,
    LinkComponent,
    RootDirective,
    TooltipDirective,
} from '@pure/hive';
import { faArrowDown, faArrowUp } from '@fortawesome/pro-light-svg-icons';
import { Subject } from 'rxjs';
import {
    EoneTierUpgradeRenderInfo,
    RECOMMENDATION_ACTION_EVENT_TOKEN,
    RecommendationActionEvent,
} from '../../../../data/models/recommendations.models';
import { registerAsDialogPlugin } from '../../services/recommendation-component-registry.service';
import { mountHighchartsChart } from '../../utils/recommendations-chart-utils';
import { Theme } from '../../../../ui/styles/theme';

/** Stable registry key for this plugin. Used as the lookup name on the corresponding RenderInfo. */
export const EONE_TIER_UPGRADE_DIALOG_PLUGIN_NAME = 'EoneTierUpgradeDialogPluginComponent';

/**
 * Dialog component for Evergreen//One tier upgrade recommendations.
 *
 * Displayed when a user clicks on a tier upgrade recommendation card. Shows
 * detailed information about the recommended license upgrade plus a toggleable
 * Highcharts load timeseries chart. Chart mounting / lifecycle is delegated to
 * `mountHighchartsChart`; `RecommendationProcessingService` bakes all data,
 * series-split, and styling into `renderInfo.chartOptions` up front.
 *
 * To create a dialog for a new recommendation type, see: recommendations-container/README.md
 */
@Component({
    selector: 'eone-tier-upgrade-dialog-plugin',
    templateUrl: './eone-tier-upgrade-dialog-plugin.component.html',
    styleUrls: ['./eone-tier-upgrade-dialog-plugin.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ButtonComponent,
        DatePipe,
        IconComponent,
        InfoIconComponent,
        LinkComponent,
        RootDirective,
        TooltipDirective,
    ],
    encapsulation: ViewEncapsulation.None,
})
export class EoneTierUpgradeDialogPluginComponent {
    renderInfo = input.required<EoneTierUpgradeRenderInfo>();

    readonly showChart = signal(false);
    readonly faArrowUp = faArrowUp;
    readonly faArrowDown = faArrowDown;
    readonly RecommendationActionEvent = RecommendationActionEvent;

    // Legend swatch styles sourced from the same theme values the chart series use,
    // so the legend can't drift from the chart if the theme palette changes.
    protected readonly legendSwatchStyles = {
        currentHistorical: { 'background-color': Theme.recommendations.eoneDialogChart.warning },
        currentProjected: {
            'border-top': `2px dashed ${Theme.recommendations.eoneDialogChart.warning}`,
            height: '0',
        },
        recommendedProjected: {
            'border-top': `2px dashed ${Theme.recommendations.eoneDialogChart.recommendedLine}`,
            height: '0',
        },
    };

    readonly chartContainer = viewChild<ElementRef<HTMLDivElement>>('chartContainer');

    private readonly recommendationActionEvent$ = inject<Subject<RecommendationActionEvent>>(
        RECOMMENDATION_ACTION_EVENT_TOKEN,
        { optional: true },
    );

    constructor() {
        mountHighchartsChart(
            this.chartContainer,
            computed(() => this.renderInfo().chartOptions),
        );
    }

    toggleChart(event: MouseEvent): void {
        this.showChart.update(v => !v);
        (event.target as HTMLElement).blur();
    }

    handleActionEvent(action: RecommendationActionEvent): void {
        this.recommendationActionEvent$?.next(action);
    }
}

registerAsDialogPlugin(EONE_TIER_UPGRADE_DIALOG_PLUGIN_NAME, EoneTierUpgradeDialogPluginComponent);
